import {Link, useNavigate, useParams} from 'react-router-dom';
import {useFetch} from '../../hooks/useFetch';
import LoadingContent, {ErrorContent} from '../../components/LoadingContent';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Chip,
    Collapse,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import {useTheme} from '@emotion/react';
import {differenceInMonths, isSameDay, parseISO, startOfDay} from 'date-fns';
import {useFormik} from 'formik';
import {useEffect, useState} from 'react';
import useHandleErrors from '../../hooks/useHandleErrors';
import {DateCalendar, PickersDay} from '@mui/x-date-pickers';
import {
    AccountCircle,
    AddCircle,
    AssignmentInd,
    AttachEmail,
    CheckCircle,
    Close,
    ExpandMore,
    Instagram,
    LocationCity,
    School,
} from '@mui/icons-material';
import * as Yup from 'yup';
import LoadingButton from '@mui/lab/LoadingButton';
import server from '../../global/server';
import {useSnackbar} from 'notistack';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';

const Schema = Yup.object({
    schedule_date: Yup.string().required(),
});

const formatarData = valor =>
    valor ? new Date(valor).toLocaleDateString('pt-BR', {day: '2-digit', month: '2-digit', year: 'numeric'}) : '';

/**
 * Card de encontro no perfil do anfitriao.
 *
 * A descricao fica recolhida atras da setinha: o titulo sozinho nao diz do que
 * o encontro trata, mas abrir todas de uma vez transformaria a lista numa
 * parede de texto.
 *
 * `passado` muda o que o card oferece: encontro que ja aconteceu nao tem o que
 * comprar, entao no lugar do botao vai a data em que ocorreu.
 */
const CardDoEncontro = ({meeting, passado, theme, onDetalhes, onAoVivo}) => {
    const [aberto, setAberto] = useState(false);
    const temDescricao = Boolean(meeting.description?.trim());

    return (
        <Card
            sx={{
                border: `1px solid ${theme.palette.grey[400]}`,
                backgroundColor: theme.palette.grey[50],
                ...(passado ? {opacity: 0.85} : {}),
            }}>
            <CardHeader
                title={meeting.title}
                sx={{fontWeight: 'bold', pb: 0}}
                action={
                    temDescricao ? (
                        <IconButton
                            onClick={() => setAberto(atual => !atual)}
                            aria-label={aberto ? 'Ocultar descrição' : 'Ver descrição'}
                            sx={{
                                transform: aberto ? 'rotate(180deg)' : 'none',
                                transition: 'transform 0.2s',
                            }}>
                            <ExpandMore />
                        </IconButton>
                    ) : null
                }
            />

            <Typography variant="body2" sx={{ml: 2, mt: 1}}>
                {meeting.pricing ? `Preço: R$ ${meeting.pricing.toFixed(2)}` : 'Gratuito'}
            </Typography>

            {temDescricao && (
                <Collapse in={aberto} unmountOnExit>
                    <CardContent sx={{pt: 1, pb: 0}}>
                        <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'pre-line'}}>
                            {meeting.description}
                        </Typography>
                    </CardContent>
                </Collapse>
            )}

            <CardActions sx={{justifyContent: 'space-between', flexWrap: 'wrap', rowGap: 1}}>
                <Stack direction="row" columnGap={1} flexWrap="wrap" rowGap={1}>
                    {meeting.ao_vivo && (
                        <Chip size="small" color="error" label="Ao vivo agora" />
                    )}
                    {meeting.raffle ? (
                        <Chip size="small" label="Seleção" />
                    ) : (
                        <Chip size="small" label="Agendamento" />
                    )}
                    {meeting.visible_public ? (
                        <Chip size="small" label="Público" />
                    ) : (
                        <Chip size="small" label="Privado" />
                    )}
                    {!passado && meeting.horarios_disponiveis === 0 && !meeting.ao_vivo && (
                        <Chip size="small" label="Sem horários livres" />
                    )}
                </Stack>

                {passado ? (
                    <Typography variant="caption" color="text.secondary">
                        {meeting.realizacoes > 1
                            ? `${meeting.realizacoes} realizações · a última em ${formatarData(meeting.ultimo_horario)}`
                            : `Realizado em ${formatarData(meeting.ultimo_horario)}`}
                    </Typography>
                ) : meeting.ao_vivo ? (
                    <Button color="error" variant="contained" onClick={() => onAoVivo(meeting)}>
                        Assistir ao vivo
                    </Button>
                ) : (
                    <Button
                        disabled={!meeting.horarios_disponiveis}
                        onClick={() => onDetalhes(meeting)}
                        variant="contained">
                        Ver detalhes
                    </Button>
                )}
            </CardActions>
        </Card>
    );
};

export default () => {
    const {username} = useParams();
    const [errs, setErrs] = useState();
    useHandleErrors(errs);
    const [datesDialog, setDatesDialog] = useState(false);
    const theme = useTheme();
    const navigation = useNavigate();
    const {data, mutate, error, isLoading} = useFetch(`host/${username}`);
    const {enqueueSnackbar} = useSnackbar();
    const [mudandoAcompanhamento, setMudandoAcompanhamento] = useState(false);

    // Acompanhamento de Anfitrioes (Termos 1.12): quem acompanha recebe aviso
    // de encontro novo, com valor e horarios. Desfazer e imediato -- a clausula
    // garante deixar de acompanhar a qualquer momento.
    const acompanhando = Boolean(data?.profile?.acompanhando);

    const alternarAcompanhamento = async () => {
        try {
            setMudandoAcompanhamento(true);
            const rota = `/host/${username}/acompanhar`;
            const {data: resposta} = acompanhando
                ? await server.delete(rota)
                : await server.post(rota);
            enqueueSnackbar(resposta.message, {variant: 'success'});
            mutate();
        } catch (erro) {
            setErrs(erro);
        } finally {
            setMudandoAcompanhamento(false);
        }
    };

    if (isLoading) return <LoadingContent />;
    if (error) return <ErrorContent labe="Aconteceu um erro. Volte mais tardes" />;

    return (
        <Container maxWidth="sm" sx={{py: 3}}>
            <Stack direction="column" rowGap={3}>
                <Box
                    sx={{
                        border: `1px solid ${theme.palette.grey[400]}`,
                        borderRadius: 2,
                        p: 2,
                        backgroundColor: theme.palette.grey[50],
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                    }}>
                    <Stack direction="row" spacing={2}>
                        <Avatar src={data?.profileImage} sx={{width: 80, height: 80}} />
                        <div>
                            <Typography variant="h5" style={{fontWeight: 800}}>
                                {data.name}
                            </Typography>
                            <Typography variant="subtitle1">
                                <Chip
                                    size="small"
                                    label={`@${data?.username}`}
                                    icon={<AccountCircle />}
                                />
                            </Typography>
                            <Stack direction="row" gap={3} my={2}>
                                <Stack alignItems="center">
                                    <Typography style={{fontSize: 13, fontWeight: 'bold'}}>
                                        Encontros
                                    </Typography>
                                    <Typography style={{fontWeight: 'bold'}}>
                                        {(data?.profile?.meeting?.length ?? 0) +
                                            (data?.profile?.meeting_anteriores?.length ?? 0)}
                                    </Typography>
                                </Stack>
                                <Stack alignItems="center">
                                    <Typography style={{fontSize: 13, fontWeight: 'bold'}}>
                                        Seguidores
                                    </Typography>
                                    <Typography style={{fontWeight: 'bold'}}>
                                        {data?.profile?.seguidores ?? 0}
                                    </Typography>
                                </Stack>
                                <Stack alignItems="center">
                                    <Typography style={{fontSize: 13, fontWeight: 'bold'}}>
                                        Convivas
                                    </Typography>
                                    <Typography style={{fontWeight: 'bold'}}>
                                        {data.numOrders}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </div>
                    </Stack>

                    <Stack justifyContent="center">
                        {Boolean(data.profession) && (
                            <Stack direction="row" alignItems="center" gap={1}>
                                <AssignmentInd fontSize="3" />
                                <Typography variant="body1">{data.profession}</Typography>
                            </Stack>
                        )}
                        {Boolean(data.education) && (
                            <Stack direction="row" alignItems="center" gap={1}>
                                <School fontSize="3" />
                                <Typography variant="body1">{data.education}</Typography>
                            </Stack>
                        )}
                        {Boolean(data.residence) && (
                            <Stack direction="row" alignItems="center" gap={1}>
                                <LocationCity fontSize="3" />
                                <Typography variant="body1">{data.residence}</Typography>
                            </Stack>
                        )}
                        {Boolean(data.email) && (
                            <Stack direction="row" alignItems="center" gap={1}>
                                <AttachEmail fontSize="3" />
                                <Typography variant="body1">{data.email}</Typography>
                            </Stack>
                        )}
                        {Boolean(data.instagram) && (
                            <Stack direction="row" alignItems="center" gap={1}>
                                <Instagram fontSize="3" />
                                <Typography variant="body1">{data.instagram}</Typography>
                            </Stack>
                        )}
                    </Stack>
                    <Typography mt={2} variant="body1">
                        {data.description}
                    </Typography>

                    {/* O anfitriao ve o botao no proprio perfil? Nao: seguir a si
                        mesmo nao faz sentido e o servidor recusa. */}
                    {!data.profile?.isOwner && (
                        <LoadingButton
                            fullWidth
                            loading={mudandoAcompanhamento}
                            variant={acompanhando ? 'outlined' : 'contained'}
                            onClick={alternarAcompanhamento}
                            startIcon={
                                acompanhando ? (
                                    <NotificationsActiveOutlinedIcon />
                                ) : (
                                    <NotificationsNoneOutlinedIcon />
                                )
                            }
                            sx={{
                                mt: 2,
                                height: 44,
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: '8px',
                                ...(acompanhando
                                    ? {borderColor: '#006a61', color: '#006a61'}
                                    : {bgcolor: '#006a61', '&:hover': {bgcolor: '#005a52'}}),
                            }}>
                            {acompanhando ? 'Acompanhando' : 'Acompanhar anfitrião'}
                        </LoadingButton>
                    )}

                    <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 1}}>
                        {acompanhando
                            ? 'Você recebe um aviso quando este anfitrião cria um encontro, com o valor e os horários. Toque de novo para parar.'
                            : 'Acompanhando, você recebe um aviso quando ele criar um encontro novo.'}
                    </Typography>
                </Box>
                <Typography variant="h6">Encontros que o anfitrião está promovendo</Typography>
                {data.profile.meeting.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        Nenhum encontro em aberto no momento.
                    </Typography>
                )}
                {data.profile.meeting.map(meeting => (
                    <CardDoEncontro
                        key={meeting.id}
                        meeting={meeting}
                        theme={theme}
                        onDetalhes={item => navigation(`/encontrar/${item.id}/detalhes`)}
                        onAoVivo={item => navigation(`/lista/ao-vivo/${item.ao_vivo_schedule_id}`)}
                    />
                ))}

                {/* Encontros que ja aconteceram. Ficam numa secao propria porque
                    nao ha o que comprar neles -- antes vinham misturados aos
                    outros, com o botao de detalhes desabilitado e sem explicar
                    o porque. */}
                {Boolean(data.profile.meeting_anteriores?.length) && (
                    <>
                        <Typography variant="h6" sx={{mt: 2}}>
                            Encontros anteriormente promovidos pelo anfitrião
                        </Typography>
                        {data.profile.meeting_anteriores.map(meeting => (
                            <CardDoEncontro
                                key={meeting.id}
                                meeting={meeting}
                                passado
                                theme={theme}
                                onDetalhes={() => {}}
                                onAoVivo={() => {}}
                            />
                        ))}
                    </>
                )}
            </Stack>
        </Container>
    );
};
